import {
    Grid,
    Box,
    styled,
    TextField,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Snackbar,
    Button,
    FormHelperText,
} from '@mui/material';
import {Color} from '../../common/theme';
import {Fragment, useContext, useEffect, useState} from 'react';
import {isEmpty} from 'lodash';
import {useOnboardNewUser} from '../../api';
import {OnboardingRequestDTO, SuccessStateWithAction} from '../../api/models';
import {AuthContext} from '../../api/context';

const BackgroundBoxWrapper = styled(Box)<{backgroundColor: string}>(({backgroundColor}) => ({
    paddingTop: 108,
    paddingBottom: 84,
    paddingLeft: 54,
    paddingRight: 54,
    margin: 0,
    backgroundColor: backgroundColor,
}));

const AboutBackgroundBox = styled(Box)({
    paddingBottom: 54,
    paddingTop: 54,
    paddingLeft: 54,
    paddingRight: 54,
    margin: 0,
    backgroundColor: Color.WHITE,
});

const FooterWrapper = styled(Box)({
    paddingBottom: 20,
    paddingTop: 20,
    paddingLeft: 108,
    paddingRight: 108,
    margin: 0,
    backgroundColor: Color.SEA_GREEN,
});

const HeroSection = () => {
    const theme = {
        backgroundColor: Color.SEA_GREEN,
        primaryTextColor: Color.WHITE,
        secondaryTextColor: Color.WHITE,
    };
    return (
        <BackgroundBoxWrapper backgroundColor={theme.backgroundColor}>
            <Grid container>
                <Grid item md={5}>
                    <span
                        style={{
                            fontSize: 32,
                            color: theme.primaryTextColor,
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                        }}>
                        Welcome!
                    </span>
                </Grid>
            </Grid>
            <Grid style={{marginTop: 12}} container>
                <Grid item md={10}>
                    <span
                        style={{
                            fontSize: 16,
                            color: theme.secondaryTextColor,
                            fontWeight: 200,
                            fontFamily: 'Poppins',
                        }}>
                        Let’s get your business up and running. Fill in a few details and we’ll setup your website.
                    </span>
                </Grid>
            </Grid>
        </BackgroundBoxWrapper>
    );
};

const FormSection = () => {
    const [shopNameText, updateShopNameText] = useState<string>('');
    const [addressStringText, updateAddressText] = useState<string>('');
    const [websiteTaglineText, updateWebsiteTagline] = useState<string>('');
    const [aboutShopText, updateAboutShopText] = useState<string>('');
    const [contactNumberText, updateContactNumber] = useState<string>('');
    const [whatsappNumberText, updateWhatsappNumber] = useState<string>('');
    const [googleMapsLinkText, updateGoogleMapsLink] = useState<string>('');
    const [subDomain, updateSubdomain] = useState<string>('');
    const [isCheckboxActive, updateCheckboxActiveState] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [snackBarMessage, updateSnackbarMessage] = useState<string>('');

    const {onboardNewUser} = useOnboardNewUser();

    const handleClose = () => {
        setOpen(false);
    };

    const [action, updateAction] = useState([
        <Button size='small' onClick={handleClose}>
            Dismiss
        </Button>,
    ]);

    useEffect(() => {
        if (whatsappNumberText === '' && contactNumberText === '') {
            return;
        }
        const shouldCheckboxBeActive = whatsappNumberText === contactNumberText;
        updateCheckboxActiveState(shouldCheckboxBeActive);
    }, [whatsappNumberText, contactNumberText]);

    const checkBoxLabel = {inputProps: {'aria-label': 'Same as contact number?'}};

    const isSubdomainValid = (subDomain: string): boolean => {
        const subdomainRegex = /^[a-zA-Z][a-zA-Z0-9-]*[a-zA-Z0-9]$/;
        return subdomainRegex.test(subDomain);
    };

    const submitFormDetails = () => {
        if (
            isEmpty(shopNameText) ||
            isEmpty(websiteTaglineText) ||
            isEmpty(addressStringText) ||
            isEmpty(aboutShopText) ||
            isEmpty(contactNumberText) ||
            isEmpty(whatsappNumberText) ||
            isEmpty(googleMapsLinkText) ||
            isEmpty(subDomain)
        ) {
            updateSnackbarMessage('Please add all relevant details');
            setOpen(true);
        } else if (!isSubdomainValid(subDomain)) {
            updateSnackbarMessage(
                'Please keep the name simple. It can be your shop name and locality. There should not be any spaces',
            );
            setOpen(true);
        } else {
            const onboardingRequest: OnboardingRequestDTO = {
                shopName: shopNameText,
                websiteTagline: websiteTaglineText,
                aboutTheShop: aboutShopText,
                contactNumber: contactNumberText,
                whatsappNumber: whatsappNumberText,
                googleMapsLink: googleMapsLinkText,
                addressString: addressStringText,
                subDomain: subDomain,
            };
            onboardNewUser({
                onboardingRequestDTO: onboardingRequest,
                onError: (errorString: string) => {
                    updateSnackbarMessage(errorString);
                    setOpen(true);
                },
                onSuccess: (responseData: SuccessStateWithAction) => {
                    updateSnackbarMessage(responseData.message);
                    const openPageAction = (
                        <Button
                            size='small'
                            onClick={() => {
                                window.open(responseData.actionDetails.path);
                            }}>
                            Open
                        </Button>
                    );
                    const dashboardPageAction = (
                        <Button
                            size='small'
                            onClick={() => {
                                window.location.href = '/dashboard-screen';
                            }}>
                            Dashboard
                        </Button>
                    );
                    const newActions = [openPageAction, dashboardPageAction, ...action];
                    updateAction(newActions);
                    setOpen(true);
                },
            });
        }
    };

    const actionButtons = <Fragment>{action}</Fragment>;

    return (
        <AboutBackgroundBox>
            <Grid container>
                <Grid item md={3} />
                <Grid item md={6}>
                    <span
                        style={{
                            fontSize: 32,
                            color: Color.BLACK,
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                        }}>
                        <center>Website Details</center>
                    </span>
                </Grid>
            </Grid>
            <br />
            <br />
            <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                    <TextField
                        id={'shop-name'}
                        label='Shop Name'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={shopNameText}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateShopNameText(e.target.value);
                        }}
                    />
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'address-component'}
                        label='Shop Address'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={addressStringText}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateAddressText(e.target.value);
                        }}
                    />
                    <FormHelperText id='address-component'>
                        Your shop address so that people can find your shop easily
                    </FormHelperText>
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'website-tagline'}
                        label='Website Tagline'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={websiteTaglineText}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateWebsiteTagline(e.target.value);
                        }}
                    />
                    <FormHelperText id='website-tagline'>
                        One line about your shop, what you sell and where
                    </FormHelperText>
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'about-the-shop'}
                        label='About the shop'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={aboutShopText}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateAboutShopText(e.target.value);
                        }}
                    />
                    <FormHelperText id='about-the-shop'>
                        What is your shop about? Where is it located? What do you sell?
                    </FormHelperText>
                </Grid>
                <br />
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'contact-number'}
                        label='Contact Number'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={contactNumberText}
                        error={contactNumberText.length > 10}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateContactNumber(e.target.value);
                        }}
                    />
                </Grid>
                <FormGroup style={{marginTop: 12}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...checkBoxLabel}
                                checked={isCheckboxActive}
                                onChange={() => {
                                    if (!isCheckboxActive) {
                                        updateWhatsappNumber(contactNumberText);
                                    }
                                    updateCheckboxActiveState(!isCheckboxActive);
                                }}
                            />
                        }
                        label='Same as contact number?'
                    />
                </FormGroup>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'whatsapp-number'}
                        label='Whatsapp Number'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={whatsappNumberText}
                        error={whatsappNumberText.length > 10}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateWhatsappNumber(e.target.value);
                        }}
                    />
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'google-maps-link'}
                        label='Google Maps Link'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={googleMapsLinkText}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateGoogleMapsLink(e.target.value);
                        }}
                    />
                    <FormHelperText id='google-maps-link'>
                        Go to Google Maps, find your shop on maps and click share to copy location link.
                    </FormHelperText>
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12} sm={12} md={12}>
                    <TextField
                        id={'subdomain-link'}
                        label='Preferred name'
                        variant='outlined'
                        sx={{
                            color: Color.SEA_GREEN,
                        }}
                        value={subDomain}
                        multiline={true}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const sanitizedValue = e.target.value
                                .toLowerCase()
                                .replace(/ /g, '')
                                .replace(/[^a-z0-9-]/g, '');
                            updateSubdomain(sanitizedValue);
                        }}
                    />
                    <FormHelperText id='subdomain-link'>
                        No spaces in between, all lowercase. Format to follow: shopname - locality - city
                    </FormHelperText>
                </Grid>
            </Grid>
            <Grid container style={{marginTop: 32}}>
                <Grid item md={5} />
                <Grid item md={2}>
                    <Box
                        style={{
                            backgroundColor: Color.FELDGRAU,
                            paddingLeft: 48,
                            paddingRight: 48,
                            paddingTop: 15,
                            paddingBottom: 15,
                            borderRadius: 32,
                            cursor: 'pointer',
                        }}>
                        <div
                            onClick={submitFormDetails}
                            style={{
                                fontSize: 15,
                                color: Color.ASH_GRAY,
                                fontWeight: 700,
                                fontFamily: 'Poppins',
                            }}>
                            <center>Submit</center>
                        </div>
                    </Box>
                </Grid>
            </Grid>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={open}
                action={actionButtons}
                autoHideDuration={2500}
                message={snackBarMessage}
            />
        </AboutBackgroundBox>
    );
};

const FooterComponent = () => {
    return (
        <FooterWrapper>
            <Grid container>
                <Grid item sm={12} xs={12} md={12}>
                    <span
                        style={{
                            fontSize: 16,
                            color: Color.WHITE,
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                        }}>
                        <center>&copy; 2024</center>
                    </span>
                </Grid>
            </Grid>
        </FooterWrapper>
    );
};

export const OnboardingScreen = () => {
    const context = useContext(AuthContext);

    useEffect(() => {
        const fetchAndUpdateUserDetails = async () => {
            const sessionDetails = await context?.fetchSessionDetails();
            if (sessionDetails) {
                context?.setUserDetails(sessionDetails);
            }
        };

        if (context?.userDetails) {
            context.setUserDetails(context.userDetails);
        } else {
            fetchAndUpdateUserDetails();
        }
    }, [context]);
    return (
        <>
            <HeroSection />
            <FormSection />
            <FooterComponent />
        </>
    );
};
